/** Query keys for cycle boards and project matrix (FE-P08). */
export const METRIC_FILTER_KEYS = [
    'notices',
    'leads',
    'meetings',
    'bookings',
    'deals',
];
/** Short URL keys for У / Л / В / Б / С. */
export const METRIC_QUERY_KEYS = {
    notices: 'u',
    leads: 'l',
    meetings: 'v',
    bookings: 'b',
    deals: 's',
};
export const METRIC_LABELS = {
    notices: 'У',
    leads: 'Л',
    meetings: 'В',
    bookings: 'Б',
    deals: 'С',
};
export const DEFAULT_METRIC_FILTERS = {
    notices: 'all',
    leads: 'all',
    meetings: 'all',
    bookings: 'all',
    deals: 'all',
};
export const DEFAULT_RATING_FILTERS = {
    ratingMin: '',
    ratingMax: '',
    engagementMin: '',
    engagementMax: '',
    externalMin: '',
    externalMax: '',
};
const RATING_QUERY_KEYS = [
    'raMin',
    'raMax',
    'rvMin',
    'rvMax',
    'vrMin',
    'vrMax',
];
const BOARD_SORT_VALUES = [
    'age-desc',
    'age-asc',
    'rating-desc',
    'rating-asc',
    'engagement-desc',
    'engagement-asc',
    'external-desc',
    'external-asc',
];
export const BOARD_SORT_OPTIONS = [
    { value: 'age-desc', label: 'Дольше на этапе' },
    { value: 'age-asc', label: 'Меньше на этапе' },
    { value: 'rating-desc', label: 'Р. Агента: по убыванию' },
    { value: 'rating-asc', label: 'Р. Агента: по возрастанию' },
    { value: 'engagement-desc', label: 'Р. Вовл.: по убыванию' },
    { value: 'engagement-asc', label: 'Р. Вовл.: по возрастанию' },
    { value: 'external-desc', label: 'Внешний: по убыванию' },
    { value: 'external-asc', label: 'Внешний: по возрастанию' },
];
const FILTER_KEYS = [
    'q',
    'responsible',
    'agency',
    'status',
    'mine',
    'project',
    'metricScope',
    'priority',
    'otherProjectNotices',
    'sort',
    ...RATING_QUERY_KEYS,
    ...Object.values(METRIC_QUERY_KEYS),
];
function parseSort(raw) {
    if (raw && BOARD_SORT_VALUES.includes(raw)) {
        return raw;
    }
    return 'default';
}
function parseStatus(raw) {
    if (raw === 'closed' || raw === 'all' || raw === 'active')
        return raw;
    return 'active';
}
function parseMetric(raw) {
    if (raw === 'yes' || raw === 'no')
        return raw;
    return 'all';
}
function parseOptionalNumber(raw) {
    if (raw == null)
        return '';
    const trimmed = raw.trim();
    if (!trimmed)
        return '';
    const n = Number(trimmed);
    if (!Number.isFinite(n))
        return '';
    return String(n);
}
function parseRatingFilters(params) {
    return {
        ratingMin: parseOptionalNumber(params.get('raMin')),
        ratingMax: parseOptionalNumber(params.get('raMax')),
        engagementMin: parseOptionalNumber(params.get('rvMin')),
        engagementMax: parseOptionalNumber(params.get('rvMax')),
        externalMin: parseOptionalNumber(params.get('vrMin')),
        externalMax: parseOptionalNumber(params.get('vrMax')),
    };
}
export function parseBoardFilters(params) {
    return {
        q: params.get('q')?.trim() ?? '',
        responsible: params.get('responsible')?.trim() || 'all',
        agency: params.get('agency')?.trim() || 'all',
        status: parseStatus(params.get('status')),
        mine: params.get('mine') === '1' || params.get('mine') === 'true',
        ...parseRatingFilters(params),
    };
}
export function parseMatrixFilters(params) {
    const base = parseBoardFilters(params);
    const metrics = { ...DEFAULT_METRIC_FILTERS };
    for (const key of METRIC_FILTER_KEYS) {
        metrics[key] = parseMetric(params.get(METRIC_QUERY_KEYS[key]));
    }
    const metricScope = params.get('metricScope') === 'all' ? 'all' : 'selected';
    return {
        ...base,
        project: params.get('project')?.trim() ?? '',
        metricScope,
        metrics,
        priority: params.get('priority') === '1' || params.get('priority') === 'true',
        otherProjectNotices: params.get('otherProjectNotices') === '1' ||
            params.get('otherProjectNotices') === 'true',
        sort: parseSort(params.get('sort')),
    };
}
export function parseBoardSort(params) {
    return parseSort(params.get('sort'));
}
function writeRatingFilters(params, filters) {
    setOrDelete(params, 'raMin', filters.ratingMin);
    setOrDelete(params, 'raMax', filters.ratingMax);
    setOrDelete(params, 'rvMin', filters.engagementMin);
    setOrDelete(params, 'rvMax', filters.engagementMax);
    setOrDelete(params, 'vrMin', filters.externalMin);
    setOrDelete(params, 'vrMax', filters.externalMax);
}
/** Write board filters into params. Keeps unrelated keys (view, agentScope). */
export function writeBoardFilters(params, filters) {
    const next = new URLSearchParams(params);
    setOrDelete(next, 'q', filters.q.trim());
    setOrDelete(next, 'responsible', filters.responsible === 'all' ? '' : filters.responsible);
    setOrDelete(next, 'agency', filters.agency === 'all' ? '' : filters.agency);
    if (filters.status === 'active')
        next.delete('status');
    else
        next.set('status', filters.status);
    if (filters.mine)
        next.set('mine', '1');
    else
        next.delete('mine');
    writeRatingFilters(next, filters);
    return next;
}
export function writeBoardSort(params, sort) {
    const next = new URLSearchParams(params);
    if (sort === 'default')
        next.delete('sort');
    else
        next.set('sort', sort);
    return next;
}
export function writeMatrixFilters(params, filters) {
    const next = writeBoardFilters(params, filters);
    setOrDelete(next, 'project', filters.project);
    if (filters.metricScope === 'selected')
        next.delete('metricScope');
    else
        next.set('metricScope', 'all');
    for (const key of METRIC_FILTER_KEYS) {
        const value = filters.metrics[key];
        const queryKey = METRIC_QUERY_KEYS[key];
        if (value === 'all')
            next.delete(queryKey);
        else
            next.set(queryKey, value);
    }
    if (filters.priority)
        next.set('priority', '1');
    else
        next.delete('priority');
    if (filters.otherProjectNotices)
        next.set('otherProjectNotices', '1');
    else
        next.delete('otherProjectNotices');
    if (filters.sort === 'default')
        next.delete('sort');
    else
        next.set('sort', filters.sort);
    return next;
}
/** Clear FE-P08 filter keys. Keep view / agentScope / trajectory. */
export function clearBoardFilterParams(params) {
    const next = new URLSearchParams(params);
    for (const key of FILTER_KEYS)
        next.delete(key);
    return next;
}
export function ratingFiltersActive(filters) {
    return (Boolean(filters.ratingMin) ||
        Boolean(filters.ratingMax) ||
        Boolean(filters.engagementMin) ||
        Boolean(filters.engagementMax) ||
        Boolean(filters.externalMin) ||
        Boolean(filters.externalMax));
}
export function boardFiltersActive(filters) {
    return (Boolean(filters.q.trim()) ||
        filters.responsible !== 'all' ||
        filters.agency !== 'all' ||
        filters.status !== 'active' ||
        filters.mine ||
        ratingFiltersActive(filters));
}
export function matrixFiltersActive(filters) {
    if (boardFiltersActive(filters))
        return true;
    if (filters.priority)
        return true;
    if (filters.otherProjectNotices)
        return true;
    if (filters.sort !== 'default')
        return true;
    if (filters.metricScope === 'all')
        return true;
    return METRIC_FILTER_KEYS.some((key) => filters.metrics[key] !== 'all');
}
/** Toggle column sort: same field flips asc/desc; new field starts at desc. */
export function toggleBoardSort(current, field) {
    const desc = `${field}-desc`;
    const asc = `${field}-asc`;
    if (current === desc)
        return asc;
    return desc;
}
function setOrDelete(params, key, value) {
    if (value)
        params.set(key, value);
    else
        params.delete(key);
}
